import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {CalendarsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {StartEndData} from '../../../../../srcCommon/helpers/calendarHelpers';
import {buildCompleteMeetingRoomName} from '../../../../helpers/models/meetingRoomHelpers';
import {buildVisualParticipantFromMeetingRoom} from '../../../../helpers/models/participantHelpers';
import {CalendarModel} from '../../../../models/api/calendarModel';
import {MeetingRoomModel} from '../../../../models/api/meetingRoomModel';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {CalendarEventParticipantsList} from '../../participants/calendarEventParticipantsList';
import {MeetingRoomsDropdownBoxContainer} from './meetingRoomsDropdownBoxContainer';

/*
 * Props.
 */

interface MeetingRoomsEditorProps {
  panelId: CalendarsPanelId;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  /* The start and end of the event we're adding a meeting room to. */
  startEndData: StartEndData;
  /* The channelId of the calendarVersion we're adding an event to. */
  channelId: number;
  location: string;
  calendar: CalendarModel;
  onLocationChange?: (arg: string) => void;
  /* A list of participants that cannot be edited, if for example the user can invite new participants but not edit existing ones. */
  readonlyContactUids?: ReadonlyArray<string>;
  /* Change handler for participants. If none is specified, the editor becomes read-only. */
  onVisualParticipantsChange?: (arg: ReadonlyArray<VisualParticipantModel>) => void;
}

/*
 * Styles.
 */

const StyledMeetingRoomsDropdownBoxContainer = styled(MeetingRoomsDropdownBoxContainer)`
  margin: 0px;
`;

const StyledListWrapperDiv = styled.div`
  margin-top: 8px;
  margin-bottom: 12px;
`;

/*
 * Component.
 */

export const MeetingRoomsEditor: FC<MeetingRoomsEditorProps> = (props) => (
  <div>
    {props.onVisualParticipantsChange && (
      <StyledMeetingRoomsDropdownBoxContainer
        onMeetingRoomSelect={(meetingRoom) => onMeetingRoomSelect(props, meetingRoom)}
        startEndData={props.startEndData}
        channelId={props.channelId}
        roomAddressesToExclude={props.visualParticipants.map((p) => p.contact.handle)}
      />
    )}
    {props.visualParticipants.length > 0 && (
      <StyledListWrapperDiv>
        <CalendarEventParticipantsList
          panelId={props.panelId}
          visualParticipants={props.visualParticipants}
          onVisualParticipantsChange={
            props.onVisualParticipantsChange
              ? (visualParticipants) => onParticipantsChange(props, visualParticipants)
              : undefined
          }
          readonlyContactUids={props.readonlyContactUids}
          shouldShowAllParticipants
        />
      </StyledListWrapperDiv>
    )}
  </div>
);

function onParticipantsChange(
  props: MeetingRoomsEditorProps,
  visualParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  if (!props.onVisualParticipantsChange) {
    return;
  }

  // Inform the parent component of the participants change.
  props.onVisualParticipantsChange(visualParticipants);

  // If we're able to edit the location, find the removed meeting room participant so that we can omit its name from the location field.
  if (!props.onLocationChange) {
    return;
  }

  const removedParticipant = _(props.visualParticipants).difference(visualParticipants).first();
  if (!removedParticipant || !removedParticipant.name) {
    return;
  }

  // Attempt to omit the room name with comma or just the room name.
  const newLocation = props.location
    .replace(`, ${removedParticipant.name}`, '')
    .replace(`${removedParticipant.name}, `, '')
    .replace(removedParticipant.name, '');
  props.onLocationChange(newLocation);
}

function onMeetingRoomSelect(props: MeetingRoomsEditorProps, meetingRoom: MeetingRoomModel) {
  if (!props.onVisualParticipantsChange) {
    return;
  }

  // Build the visualParticipant for the meeting room and add it to the participants list.
  props.onVisualParticipantsChange([
    ...props.visualParticipants,
    buildVisualParticipantFromMeetingRoom(meetingRoom),
  ]);

  if (!props.onLocationChange) {
    return;
  }

  // Add the meeting room name to the event location.
  const roomName = buildCompleteMeetingRoomName(meetingRoom);
  if (props.location.includes(roomName)) {
    return;
  }

  const newLocation = props.location ? `${props.location}, ${roomName}` : roomName;
  props.onLocationChange(newLocation);
}
